import {
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  Typography,
  Box,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Tooltip,
  Alert,
} from '@mui/material';
import { useState } from 'react';
import KeyIcon from '@mui/icons-material/Key';
import FunctionsIcon from '@mui/icons-material/Functions';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import { Device } from '../../../store/reducers/deviceReducers';
import { useDispatch } from 'react-redux';
import { updateFormula, updateDevice } from '../../../store/actions/deviceActions';
import deviceApi, { RecalibrationResult } from '../../../api/devices';
import ApplyFormula from './ApplyFormula';
import DeviceCalibration from './DeviceCalibration';

const DeviceInfo = ({
  device,
  onFormulaApplied,
}: {
  device: Device;
  onFormulaApplied?: () => void;
}) => {
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [filledFromWell, setFilledFromWell] = useState(false);
  const dispatch = useDispatch();

  const handleChange = (key: string, value: number) => {
    dispatch(updateFormula({ serial: device.serial, formula: { [key]: value } }));
  };

  const handleEdit = async () => {
    if (!edit) {
      setEdit(true);
      return;
    }
    setLoading(true);
    await deviceApi.putFormula(device.target_id, device.formula.id, {
      converter: device.formula.converter,
      constant: device.formula.constant,
      cable_length: device.formula.cable_length,
    })();
    setLoading(false);
    setEdit(false);
    setFilledFromWell(false);
  };

  const useCalculatedFormula = (result: RecalibrationResult) => {
    dispatch(
      updateFormula({
        serial: device.serial,
        formula: {
          converter: result.converter,
          constant: result.constant,
          cable_length: result.cable_length,
        },
      }),
    );
    setFilledFromWell(true);
    setEdit(true);
  };

  const deviceStatusToggle = () => {
    if (device.is_active) {
      dispatch({
        type: updateDevice.request,
        payload: { serial: device.serial, is_active: false },
      });
    } else {
      dispatch({
        type: updateDevice.request,
        payload: { serial: device.serial, is_active: true },
      });
    }
  };

  return (
    <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={3}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <KeyIcon color='primary' />
            <Typography variant='h6'>Device Information</Typography>
          </Stack>
          <Divider />
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant='body2' color='text.secondary'>
                Device Key
              </Typography>
              <Typography variant='body1' fontWeight='medium'>
                {device.device_key}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant='body2' color='text.secondary'>
                Serial
              </Typography>
              <Typography variant='body1' fontWeight='medium'>
                {device.serial}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Stack direction='row' alignItems='center' spacing={0.5}>
                <Typography variant='body2' color='text.secondary'>
                  Status
                </Typography>
                <Tooltip title="If disabled, the device won't show up in any charts or graphs">
                  <InfoIcon fontSize='inherit' color='action' />
                </Tooltip>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={device.is_active}
                    onChange={deviceStatusToggle}
                    color='primary'
                  />
                }
                label={device.is_active ? 'Active' : 'Inactive'}
              />
            </Grid>
          </Grid>
          <Divider />
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            flexWrap='wrap'
            spacing={1}
          >
            <Stack direction='row' alignItems='center' spacing={1}>
              <FunctionsIcon color='primary' />
              <Typography variant='h6'>Formula Configuration</Typography>
            </Stack>
            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
              <Tooltip
                title={
                  edit
                    ? 'Save the formula first. Readings use the formula stored for this device.'
                    : 'Re-convert stored liquid level readings in a time range from their raw values. Battery, signal, and other readings stay unchanged.'
                }
              >
                <span>
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<FunctionsIcon />}
                    disabled={edit || loading}
                    onClick={() => setApplyOpen(true)}
                  >
                    Apply formula
                  </Button>
                </span>
              </Tooltip>
              <DeviceCalibration
                device={device}
                disabled={loading}
                onFormulaReady={useCalculatedFormula}
              />
              <Button
                startIcon={edit ? <SaveIcon /> : <EditIcon />}
                size='small'
                variant={filledFromWell ? 'contained' : 'outlined'}
                disabled={loading}
                onClick={() => handleEdit()}
              >
                {loading ? 'Saving…' : edit ? (filledFromWell ? 'Submit' : 'Save') : 'Edit'}
              </Button>
            </Stack>
          </Stack>
          <Typography variant='body2' color='text.secondary'>
            Recalibrate fits the cable length to this well&apos;s BGwT manual readings, using the
            target name as the address unless you override it, and fills the fields below. Submit
            writes that formula. Stored readings stay unchanged until you apply the formula.
          </Typography>
          {filledFromWell && (
            <Alert severity='info'>
              Formula filled from the well reading. Submit writes these values. Nothing is saved
              yet.
            </Alert>
          )}
          <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
            <Typography variant='body2' color='text.secondary' gutterBottom>
              Sensor Calculation Formula
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
              Y is liquid level and X is the raw sensor value. Saving these values updates future
              readings. Apply formula re-converts stored liquid level readings from their raw values
              for a time range you choose. Battery, signal, and other readings stay unchanged.
            </Typography>
            <Typography
              variant='body1'
              fontFamily="'Courier New', monospace"
              fontWeight='medium'
              sx={{
                p: 1.5,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              Y = X * {device.formula.converter} + {device.formula.constant} -{' '}
              {device.formula.cable_length}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={4}>
                <Typography variant='caption' color='text.secondary'>
                  Converter
                </Typography>
                {edit ? (
                  <TextField
                    fullWidth
                    size='small'
                    value={device.formula.converter}
                    onChange={(e) => handleChange('converter', Number(e.target.value))}
                    variant='outlined'
                    margin='dense'
                    type='number'
                  />
                ) : (
                  <Typography variant='body2' fontWeight='medium'>
                    {device.formula.converter}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={4}>
                <Typography variant='caption' color='text.secondary'>
                  Constant
                </Typography>
                {edit ? (
                  <TextField
                    fullWidth
                    size='small'
                    value={device.formula.constant}
                    onChange={(e) => handleChange('constant', Number(e.target.value))}
                    variant='outlined'
                    margin='dense'
                    type='number'
                  />
                ) : (
                  <Typography variant='body2' fontWeight='medium'>
                    {device.formula.constant}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={4}>
                <Typography variant='caption' color='text.secondary'>
                  Cable Length
                </Typography>
                {edit ? (
                  <TextField
                    fullWidth
                    size='small'
                    value={device.formula.cable_length}
                    onChange={(e) => handleChange('cable_length', Number(e.target.value))}
                    variant='outlined'
                    margin='dense'
                    type='number'
                  />
                ) : (
                  <Typography variant='body2' fontWeight='medium'>
                    {device.formula.cable_length}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
          <ApplyFormula
            device={device}
            open={applyOpen}
            onClose={() => setApplyOpen(false)}
            onApplied={onFormulaApplied}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DeviceInfo;
